<template>
	<PageWrapper>
		<VectorsMobileFiber />
		<VectorsAnimatedFiber :length="1000" />
		<HeroesServiceArea
			:tracking-location="SERVICE_AREAS_PAGE_LOCATIONS.hero"
			:image="heroImage"
			:text="heroRichText"
		/>
		<ServiceAreasCarousel class="md:hidden" />
		<ServiceAreasList
			:areas="areas"
			:coming-soon-text="comingSoonText"
			:h2="h2"
			:tracking-location="SERVICE_AREAS_PAGE_LOCATIONS.main"
			class="hidden md:block"
		/>
		<LazyVisibleBannersAddressChecker
			hydrate-on-visible
			:tracking-location="SERVICE_AREAS_PAGE_LOCATIONS.ctaBanner"
		/>
	</PageWrapper>
</template>

<script setup lang="ts">
import { useErrorReporter } from '@/composables/useErrorReporter'
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'
import { SERVICE_AREAS_PAGE_LOCATIONS } from '@/constants/ga4/tracking-ids/pages/service-areas'
import {
	BASE_URL,
	COMPANY_NAME,
	COMPANY_PHONE
} from '@/constants/index.js'
import {
	JSON_LD_HAS_OFFER_CATALOG,
	JSON_LD_LOGO
} from '@/constants/json-ld'
import { OG_IMAGE_DEFAULT } from '@/constants/og-image'
import type { SvgImageProps } from '@/types/components/svg-image'
import type { HeroMarkdown } from '@/types/storyblok/hero'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const { bugsnagReport } = useErrorReporter()

const route = useRoute()

const { locale } = useI18n({
	useScope: 'global'
})

const { getStory } = useStoryblokWithErrorReporting()

const {
	data,
	error
} = await useFetch('/api/service-areas', { params: { language: locale.value } })

const noData = !data.value || !data.value?.length
if (noData) {
	bugsnagReport({
		error: new Error('204 - No service area content'),
		showToast: true,
		toast: {
			title: 'No Content: No service area content found',
			message: 'Please check the service area content in Storyblok.'
		}
	})
}
if (error.value) {
	bugsnagReport({
		error: new Error('API Error - No service areas found'),
		showToast: true,
		toast: {
			title: 'Error: No service areas found',
			message: 'Please check the service areas in Storyblok.'
		}
	})
}

const stories = computed(() => data?.value || [])

const areas = computed(() => {
	return stories?.value?.map((obj: any) => {
		const {
			title, subtitle, comingSoon, comingSoonText, image
		} = obj.content?.cityCardContent?.[0] || {
			title: '',
			subtitle: '',
			comingSoon: false,
			comingSoonText: '',
			image: ''
		}
		return {
			id: obj.uuid,
			link: `/${obj.full_slug}`,
			title,
			subtitle,
			comingSoon,
			comingSoonText,
			image
		}
	})
})

const storyblok = await getStory('service-areas')

const { story } = storyblok.value?.data || {}

const h2 = computed(() => {
	return story?.content?.h2
})

const comingSoonText = computed(() => {
	return renderRichText(story?.content?.comingSoonText) || ''
})

const heroImage = computed<SvgImageProps['image']>(() => {
	const img = areas.value?.[0]?.image
	return img
		? {
			id: img.id,
			url: img.filename,
			alt: img.alt
		}
		: {
			url: '',
			alt: ''
		}
})

const heroContent = story?.content?.heroContent?.[0]

const heroRichText = computed<HeroMarkdown>(() => {
	return heroContent
		? {
			title: heroContent?.title || undefined,
			subtitle: heroContent?.subtitle || undefined,
			description: heroContent?.description || ''
		}
		: {
			title: {
				type: 'doc',
				content: []
			}
		}
})

const areasJsonLd = computed(() => {
	return stories?.value?.map((story: any) => {
		const {
			city, stateShort
		} = story.content?.cityCardContent?.[0] || {
			city: '',
			stateShort: ''
		}
		return {
			'@type': 'Place',
			'address': {
				'@type': 'PostalAddress',
				'addressLocality': city,
				'addressRegion': stateShort
			},
			'potentialAction': {
				'@type': 'SearchAction',
				'query': `Internet services in ${city}, ${stateShort}`
			}
		}
	})
})

const {
	title, description
} = story?.content?.metatags || {
	title: 'Service Areas',
	description: 'Explore our service areas for high-speed internet coverage.'
}

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogUrl: () => `${BASE_URL}${route?.fullPath}`,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => '/images/og-image-fallback.png',
	twitterCard: () => 'summary_large_image'
})
 import.meta.prerender && defineOgImage('Default', OG_IMAGE_DEFAULT)

useHead({
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicons/favicon.ico'
		}
	]
})

useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'BreadcrumbList',
			'itemListElement': [
				{
					'@type': 'ListItem',
					'position': 1,
					'name': 'Home',
					'item': BASE_URL
				}, {
					'@type': 'ListItem',
					'position': 2,
					'name': 'Service Areas',
					'item': `${BASE_URL}${route.fullPath}`
				}
			]
		},
		{
			'@type': 'Service',
			'name': COMPANY_NAME,
			'logo': JSON_LD_LOGO,
			'provider': {
				'@type': 'Organization',
				'name': COMPANY_NAME,
				'telephone': COMPANY_PHONE
			},
			'serviceType': 'High-speed Internet',
			'serviceOutput': 'Internet service',
			'areaServed': [
				...areasJsonLd.value
			],
			'hasOfferCatalog': JSON_LD_HAS_OFFER_CATALOG
		}
	]
})
</script>
